import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styles from './ActivityHistory.module.css';
import DataFallback from './feedback/DataFallback';

const ActivityHistory = ({ 
  activities, 
  loading, 
  error
}) => {
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Helper functions - defined before useMemo
  const getActivityTitle = (activity) => {
    return activity.title || activity.name || activity.activity_name || 'Untitled Activity';
  };

  const getActivityDescription = (activity) => {
    return activity.description || activity.activity_description || 'No description available';
  };

  // Sort activities by date (newest first)
  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      const dateA = new Date(a.created_at || a.activity_date || 0);
      const dateB = new Date(b.created_at || b.activity_date || 0);
      return dateB - dateA; // Newest first
    });
  }, [activities]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Format relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    } catch (error) {
      return '';
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async (activity) => {
    if (!activity.pdf_url) return;
    
    try {
      const response = await fetch(activity.pdf_url);
      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', activity.pdf_url.split('/').pop() || 'download.pdf');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Handle view details
  const handleViewDetails = (activity) => {
    setSelectedActivity(activity);
    setShowDetailsModal(true);
  };

  // Close details modal
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedActivity(null);
  };

  // Get activity type icon and color
  const getActivityTypeInfo = (activity) => {
    if (activity.pdf_url) {
      return {
        icon: '📄',
        color: '#4CAF50',
        bgColor: '#E8F5E8',
        label: 'PDF Document'
      };
    }
    return {
      icon: '📋',
      color: '#2196F3',
      bgColor: '#E3F2FD',
      label: 'Activity'
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.historyContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading amazing activities...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    // "Oops! Something went wrong" over a raw message, with a button that threw
    // away the whole page to recover from one request.
    return (
      <div className={styles.historyContainer}>
        <DataFallback
          variant="pane"
          error={typeof error === 'string' ? { message: error, retryable: true } : error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className={styles.historyContainer}>
      {/* Activity List */}
      {sortedActivities.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📅</div>
          <h3>No Activities Found</h3>
          <p>
            No activities are available at this time.
          </p>
        </div>
      ) : (
        <div className={styles.activityGrid}>
          {sortedActivities.map((activity, index) => {
            const typeInfo = getActivityTypeInfo(activity);
            const relativeTime = getRelativeTime(activity.created_at || activity.activity_date);
            
            return (
              <div key={activity.id || index} className={styles.activityCard}>
                {/* Card Content */}
                <div className={styles.cardContent}>
                  <h3 className={styles.activityTitle}>
                    {getActivityTitle(activity)}
                  </h3>
                  
                  {getActivityDescription(activity) && (
                    <p className={styles.activityDescription}>
                      {getActivityDescription(activity)}
                    </p>
                  )}
                </div>
                
                {/* Card Actions */}
                <div className={styles.cardActions}>
                  <button 
                    className={`${styles.actionButton} ${styles.viewButton}`}
                    onClick={() => handleViewDetails(activity)}
                  >
                    View Details
                  </button>
                  
                  {activity.pdf_url && (
                    <button 
                      className={`${styles.actionButton} ${styles.downloadButton}`}
                      onClick={() => handleDownloadPDF(activity)}
                    >
                      Download PDF
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Enhanced Modal */}
      {showDetailsModal && selectedActivity && (
        <div className={styles.modalOverlay} onClick={handleCloseDetails}>
          <div className={styles.detailsModal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleSection}>
                <div className={styles.modalIcon}>
                  {getActivityTypeInfo(selectedActivity).icon}
                </div>
                <div>
                  <h2>Calendar Details</h2>
                  <p className={styles.modalSubtitle}>
                    {getActivityTypeInfo(selectedActivity).label}
                  </p>
                </div>
              </div>
              <button className={styles.closeButton} onClick={handleCloseDetails}>
                ✕
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.detailSection}>
                <h3 className={styles.detailSectionTitle}>📝 Calendar Information</h3>
                <div className={styles.detailInfo}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Title:</span>
                    <span className={styles.detailValue}>{getActivityTitle(selectedActivity)}</span>
                  </div>
                  {selectedActivity.updated_at && selectedActivity.updated_at !== selectedActivity.created_at && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Updated:</span>
                      <span className={styles.detailValue}>{formatDate(selectedActivity.updated_at)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {getActivityDescription(selectedActivity) && (
                <div className={styles.detailSection}>
                  <h3 className={styles.detailSectionTitle}>📄 Description</h3>
                  <p className={styles.detailDescription}>
                    {getActivityDescription(selectedActivity)}
                  </p>
                </div>
              )}
              
              {selectedActivity.pdf_url && (
                <div className={styles.detailSection}>
                  <h3 className={styles.detailSectionTitle}>📎 Attached Document</h3>
                  <div className={styles.pdfActions}>
                    <a 
                      href={selectedActivity.pdf_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={styles.viewPdfButton}
                    >
                      View PDF
                    </a>
                    <button
                      className={styles.downloadPdfButton}
                      onClick={() => handleDownloadPDF(selectedActivity)}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              )}
              
              <div className={styles.modalActions}>
                <button className={styles.closeModalButton} onClick={handleCloseDetails}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityHistory;
